/**
 * Dashboard context — provides dashboard state + analytics data.
 *
 * New components use: const { dashState, loading } = useDashboard();
 * Existing NectarApp.jsx continues using its own useState until migrated.
 *
 * Usage:
 *   import { useDashboard } from '../context/DashboardContext';
 *   const { dashState, loading, refresh } = useDashboard();
 */

import { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

const DashboardContext = createContext(null);

export function DashboardProvider({ children }) {
  const [dashState, setDashState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const token = api.getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/app/api/user/dashboard-state');
      if (data) setDashState(data);
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Derived values
  const followerCount = dashState?.follower_count || 500;
  const impressionsPerPost = Math.max(1, Math.round(followerCount * 0.0022));
  const postsPerWeek = dashState?.posts_per_week || 3;
  const weeklyReach = impressionsPerPost * postsPerWeek;
  const isTrialing = !!dashState?.is_trialing;
  const trialDaysLeft = dashState?.trial_days_remaining ?? 0;
  const plan = (dashState?.plan || 'presence').toLowerCase();

  return (
    <DashboardContext.Provider value={{
      dashState, setDashState, loading, error, refresh,
      followerCount, impressionsPerPost, postsPerWeek, weeklyReach,
      isTrialing, trialDaysLeft, plan,
    }}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const ctx = useContext(DashboardContext);
  if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
  return ctx;
}

export default DashboardContext;
